import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useStore, type DefaultRootState } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import {
  skipToken,
  type TypedUseQueryStateResult,
} from '@reduxjs/toolkit/query/react';
import { chain } from 'lodash';
import { type IconName } from '@bbkAdminComponents/icons/icon-generator';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/use-show-demo-data';
import { type RTESuggestion } from '@bbkAdminComponents/rich-text-editor';
import { selectAppId, selectCompany } from '@bbkAdminRedux/app/selectors';
import {
  AppFieldType,
  FieldTypes,
  IntegrationDataType,
  IntegrationFieldTypeEnum,
  IntegrationTypeEnum,
  type Integration,
  type IntegrationField,
  type IntegrationFieldsResponse,
  type IntegrationFieldWithId,
} from '@bbkAdminRedux/data-management/reducers';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import {
  bbkApiSlice,
  type BBK_FETCH_BASE_QUERY,
} from '@bbkAdminRedux/rtkq/bbkApi.slice';
import {
  type AppKey,
  type DateString,
} from '@bbkAdminRedux/rtkq/current-user.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import {
  canAccessSalesforceIntegration,
  useGetProductLicense,
} from '@bbkAdminUtils/plans';
import { buildIntegrationFieldCatalog } from './integration-field-catalog';

export const ChargebeeJSIntKey = 'apiAndChargebeejs';

export const ChargebeeJsIntDisplayName = 'API & Chargebee.js';
export const BrightbackJsIntDisplayName = 'API & JS SDK';

export type IntegrationAPIInput = {
  appId: string;
  integrationId: string;
  inputs?: Record<string, string>;
};

export type DisconnectIntegrationAPIInput = IntegrationAPIInput & {
  testSiteOnly?: boolean;
};

enum StandardFieldType {
  TEXT = 'TEXT',
  BOOLEAN = 'BOOLEAN',
  INTEGER = 'INTEGER',
  DECIMAL = 'DECIMAL',
  DATE = 'DATE',
  EMAIL = 'EMAIL',
  URL = 'URL',
}

export type StandardField = {
  id: number;
  description: string;
  active: boolean;
  created_at: DateString;
  updated_at: DateString;
  internal_name: string;
  name: string;
  order: number;
  type: StandardFieldType;
};

export type StandardFieldsGroup = {
  group: string;
  name: string;
  order: number;
  fields: StandardField[];
};

export type FieldMapping = {
  name: string;
  display_name: string;
  standard: boolean;
  integration_id: number;
  type: IntegrationFieldTypeEnum;
  include_in_customers_report: boolean;
  field: {
    name: string;
    object?: string;
    is_lov?: boolean;
    type?: IntegrationFieldTypeEnum;
    maps_to_type: IntegrationFieldTypeEnum[];
  };
};

export type FieldMappingsResponse = FieldMapping[];
export type FieldMappingWrite = Omit<FieldMapping, 'type' | 'field'> & {
  type?: IntegrationFieldTypeEnum;
  field: Pick<FieldMapping['field'], 'name'> & Partial<FieldMapping['field']>;
};
// Share only request status across editor lifetimes; the draft is page-local.
export const fieldMappingsSaveKey = (appKey: string) =>
  `field-mappings:${appKey}`;

export type IntegrationFieldResponse = {
  key: string;
  display_name: string;
}[];

type GetIntegrationsArgs = { appKey: AppKey };
type GetFieldMappingsArgs = { appKey: AppKey };
type GetStandardFieldsArgs = { companyKey: string; appKey: string };

type GetIntegrationFieldsArgs = {
  appKey: AppKey;
  integrationDataType: IntegrationDataType;
};

type SetExternalIdArgs = {
  appKey: AppKey;
  integrationDataType: IntegrationDataType;
  externalIdObjectName: string;
} & {
  external_id: string;
};

export type CustomFieldBody = {
  objects: [
    {
      fields: IntegrationField[];
    },
  ];
};
type SaveCustomFieldsArgs = {
  appKey: AppKey;
  integrationDataType: IntegrationDataType;
  body: CustomFieldBody;
};

type GetCustomFieldsArgs = {
  appKey: AppKey;
};

type CustomField = `custom.${string}`;
type CustomFieldsResponse = {
  custom_field_names: CustomField[];
};
type GetThirdPartyIntegrationManageUrlArgs = {
  appKey: string;
  integrationId: number;
  redirectUrl: string;
};
export const integrationSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getThirdPartyIntegrationManageUrl: build.query<
      { url: string },
      GetThirdPartyIntegrationManageUrlArgs
    >({
      query: ({ appKey, integrationId, redirectUrl }) =>
        `/admin/app/${appKey}/integration/${integrationId}/manage?redirectURL=${redirectUrl}`,
    }),
    getStandardFields: build.query<
      StandardFieldsGroup[],
      GetStandardFieldsArgs
    >({
      query: ({ companyKey, appKey }) =>
        `/api/standard_fields/companies/${companyKey}/apps/${appKey}`,
    }),
    getIntegrationFields: build.query<
      IntegrationFieldsResponse,
      GetIntegrationFieldsArgs
    >({
      query: ({ appKey, integrationDataType }) =>
        `/api/v1/apps/${appKey}/integrations/${integrationDataType.toLowerCase()}/fields?${integrationDataType.toLowerCase()}`,
      transformResponse: (response: IntegrationFieldsResponse, meta, arg) => {
        if (arg.integrationDataType !== 'BRIGHTBACKJS') {
          return response;
        }

        const fieldLabelMapping: Record<string, string> = {
          'account.internal_id': 'External User ID',
          'account.billing_id': 'Customer ID',
          'account.plan': 'Subscription Plan',
          'account.plan_term': 'Subscription Plan Term',
        };

        return {
          ...response,
          objects: response.objects.map((obj) => ({
            ...obj,
            fields: obj.fields.map((field) => ({
              ...field,
              label: fieldLabelMapping[field.name] || field.label,
            })),
          })),
        };
      },
      providesTags: (result, error, arg) => [
        {
          type: 'IntegrationFields',
          id: `${arg.appKey}:${arg.integrationDataType}`,
        },
        { type: 'IntegrationFields', id: arg.appKey },
      ],
    }),
    getCustomFields: build.query<CustomField[], GetCustomFieldsArgs>({
      query: ({ appKey }) =>
        `/api/v1/apps/${appKey}/integrations/brightbackjs/custom_field_names`,
      transformResponse: (res: CustomFieldsResponse) => res.custom_field_names,
    }),
    saveCustomFields: build.mutation<void, SaveCustomFieldsArgs>({
      query: ({ appKey, integrationDataType, body }) => ({
        url: `/api/v1/apps/${appKey}/integrations/${integrationDataType}/fields`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => {
        if (error) return [];
        return [
          {
            type: 'IntegrationFields',
            id: `${arg.appKey}:${arg.integrationDataType}`,
          },
        ];
      },
    }),
    setExternalId: build.mutation<Integration[], SetExternalIdArgs>({
      query: ({
        appKey,
        integrationDataType,
        externalIdObjectName,
        ...body
      }) => ({
        url: `/api/v1/apps/${appKey}/integrations/${integrationDataType.toLowerCase()}/fields/${externalIdObjectName}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              {
                type: 'IntegrationFields',
                id: `${arg.appKey}:${arg.integrationDataType}`,
              },
            ],
    }),
    getFieldMappings: build.query<FieldMappingsResponse, GetFieldMappingsArgs>({
      query: ({ appKey }) => `/api/v1/apps/${appKey}/mappings`,
      providesTags: (result, error, { appKey }) => [
        { type: 'FieldMappings', id: appKey },
      ],
    }),
    saveFieldMappings: build.mutation<
      unknown,
      { appKey: string; mappings: FieldMappingWrite[] }
    >({
      query: ({ appKey, mappings }) => ({
        url: `/api/v1/apps/${appKey}/mappings`,
        method: 'PUT',
        body: mappings,
      }),
      invalidatesTags: (result, error, { appKey }) =>
        error ? [] : [{ type: 'FieldMappings', id: appKey }],
    }),
    loadFieldValues: build.query<
      IntegrationFieldResponse,
      {
        appKey: string;
        fieldName: string;
        integrationId: number;
        text?: string;
      }
    >({
      query: ({ appKey, fieldName, integrationId, text = '' }) => ({
        url: `/api/v1/apps/${appKey}/integrations/${integrationId}/fields/${fieldName}/values`,
        params: { text },
      }),
      providesTags: ['IntegrationFieldValues'],
    }),
    getIntegrations: build.query<Integration[], GetIntegrationsArgs>({
      query: ({ appKey }) => `/api/v1/apps/${appKey}/integrations`,
      transformResponse: (response: { integrations: Integration[] }) => {
        const result = response.integrations;

        // we want chargebee be always at the top
        const chargebeeIdx = result.findIndex(
          (x) => x.data_type === IntegrationDataType.CHARGEBEE
        );
        if (chargebeeIdx !== -1) {
          const chargebee = result.splice(chargebeeIdx, 1)[0]!;
          result.unshift(chargebee);
        }
        return result;
      },
      providesTags: (result, error, { appKey }) => [
        { type: 'Integrations', id: appKey },
      ],
    }),
    putIntegration: build.mutation<void, IntegrationAPIInput>({
      query: ({ appId, integrationId, inputs }) => ({
        url: `/api/v1/apps/${appId}/integrations/${integrationId}/inputs`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { ...inputs },
      }),
      invalidatesTags: (result, error, { appId }) =>
        error
          ? []
          : [
              { type: 'Integrations', id: appId },
              { type: 'IntegrationFields', id: appId },
              'CurrentApp',
              { type: 'FieldMappings', id: appId },
              { type: 'BillingConfigs', id: 'GLOBAL' },
            ],
    }),
    deleteIntegration: build.mutation<void, DisconnectIntegrationAPIInput>({
      query: ({ appId, integrationId, testSiteOnly }) => ({
        url: `/admin/app/${appId}/integration/${integrationId}/delete`,
        method: 'DELETE',
        ...(testSiteOnly && { params: { testSiteOnly } }),
      }),
      invalidatesTags: (result, error, { appId }) =>
        error
          ? []
          : [
              { type: 'Integrations', id: appId },
              { type: 'IntegrationFields', id: appId },
              'CurrentApp',
              { type: 'FieldMappings', id: appId },
              { type: 'BillingConfigs', id: 'GLOBAL' },
            ],
    }),
  }),
});

type IntegrationSelectorArgs = TypedUseQueryStateResult<
  Integration[],
  GetIntegrationsArgs,
  typeof BBK_FETCH_BASE_QUERY
>;
export const selectBillingIntegration = (res: IntegrationSelectorArgs) => ({
  ...res,
  data: res.data?.find(
    (x) => x.integration_type === IntegrationTypeEnum.Billing && x.connected
  ),
});

export const selectBillingIntegrations = (res: IntegrationSelectorArgs) => ({
  ...res,
  data: res.data?.filter(
    (x) => x.integration_type === IntegrationTypeEnum.Billing && x.connected
  ),
});

export const useSelectBillingIntegration = (args?: GetIntegrationsArgs) => {
  const { currentData: integrations = [] } =
    integrationSlice.endpoints.getIntegrations.useQuery(
      !args || args.appKey === '_' ? skipToken : args
    );

  return useMemo(
    () =>
      integrations.find(
        (x) => x.integration_type === IntegrationTypeEnum.Billing && x.connected
      ),
    [integrations]
  );
};

export const useChargebeeSiteName = (appKey: string): string | undefined => {
  const billingIntegration = useSelectBillingIntegration({ appKey });
  return billingIntegration?.required_input?.find((x) => x.name === 'site')
    ?.value;
};

export const useSelectFieldTypes = () => useMemo(() => FieldTypes, []);

const EMPTY_INTEGRATIONS: Integration[] = [];
// ApiController.getFields only exposes field objects for these integration types.
const INTEGRATIONS_WITH_FIELDS = new Set([
  IntegrationDataType.SALESFORCE,
  IntegrationDataType.TOTANGO,
  IntegrationDataType.BRIGHTBACKJS,
  IntegrationDataType.STRIPE,
  IntegrationDataType.STRIPE_API_KEY,
  IntegrationDataType.RECHARGE,
  IntegrationDataType.RECURLY,
  IntegrationDataType.CHARGEBEE,
]);

export const useIntegrationFieldCatalog = ({ appKey }: GetIntegrationsArgs) => {
  const dispatch = useAppDispatch();
  const list = integrationSlice.endpoints.getIntegrations.useQuery({ appKey });
  const integrations = list.currentData ?? EMPTY_INTEGRATIONS;
  // Depend on query identity, not list response identity, to retain subscriptions on refetch.
  const typesKey = JSON.stringify(
    [
      ...new Set(
        integrations
          .map((integration) => integration.data_type)
          .filter((type) => INTEGRATIONS_WITH_FIELDS.has(type))
      ),
    ].sort()
  );
  const connectedKey = JSON.stringify(
    [
      ...new Set(
        integrations
          .filter(
            (integration) =>
              integration.connected &&
              INTEGRATIONS_WITH_FIELDS.has(integration.data_type)
          )
          .map((integration) => integration.data_type)
      ),
    ].sort()
  );
  const args = useMemo(
    () =>
      (JSON.parse(typesKey) as IntegrationDataType[]).map(
        (integrationDataType) => ({ appKey, integrationDataType })
      ),
    [appKey, typesKey]
  );
  const connectedArgs = useMemo(
    () =>
      (JSON.parse(connectedKey) as IntegrationDataType[]).map(
        (integrationDataType) => ({ appKey, integrationDataType })
      ),
    [appKey, connectedKey]
  );
  const selectQueries = useMemo(
    () =>
      createSelector(
        args.map((arg) =>
          integrationSlice.endpoints.getIntegrationFields.select(arg)
        ),
        (...queries) => queries
      ),
    [args]
  );
  const queries = useSelector((state: DefaultRootState) =>
    selectQueries(state)
  );

  useEffect(() => {
    const subscriptions = connectedArgs.map((arg) =>
      dispatch(integrationSlice.endpoints.getIntegrationFields.initiate(arg))
    );
    return () =>
      subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [dispatch, connectedArgs]);

  const connectedTypes = new Set(
    connectedArgs.map((arg) => arg.integrationDataType)
  );
  const connectedQueries = queries.filter((_, index) =>
    connectedTypes.has(args[index]!.integrationDataType)
  );
  const fields = useMemo(
    () =>
      queries.flatMap((query, index) =>
        query.data &&
        connectedArgs.some(
          (arg) => arg.integrationDataType === args[index]!.integrationDataType
        )
          ? [query.data]
          : []
      ),
    [queries, connectedArgs, args]
  );
  const flatFields = useMemo(
    () =>
      fields.flatMap((integration) =>
        integration.objects.flatMap((object) =>
          object.fields.map((field) => ({
            ...field,
            integration_id: integration.id,
          }))
        )
      ),
    [fields]
  );
  const catalog = useMemo(
    () =>
      buildIntegrationFieldCatalog(
        integrations,
        Object.fromEntries(
          args.map((arg, index) => [
            arg.integrationDataType,
            queries[index]?.data,
          ])
        )
      ),
    [integrations, args, queries]
  );
  const errors = Object.fromEntries(
    args.flatMap((arg, index) =>
      connectedTypes.has(arg.integrationDataType) && queries[index]?.error
        ? [[arg.integrationDataType, queries[index]!.error]]
        : []
    )
  );
  const retry = useCallback(() => {
    list.refetch();
    connectedArgs.forEach((arg) =>
      dispatch(
        integrationSlice.endpoints.getIntegrationFields.initiate(arg, {
          subscribe: false,
          forceRefetch: true,
        })
      )
    );
  }, [list.refetch, connectedArgs, dispatch]);
  return {
    integrations,
    catalog,
    fields,
    flatFields,
    isListSuccess: list.isSuccess && list.currentData !== undefined,
    listError: list.error,
    errors,
    isLoading:
      (!list.currentData && !list.isError) ||
      connectedQueries.some(
        (query) => !query.data && (query.isUninitialized || query.isLoading)
      ),
    isFetching:
      list.isFetching || connectedQueries.some((query) => query.isLoading),
    retry,
  };
};

export type IntegrationFieldCatalog = ReturnType<
  typeof useIntegrationFieldCatalog
>;

export const useSelectIntegrationsFields = (
  args: GetIntegrationsArgs
): IntegrationFieldsResponse[] => useIntegrationFieldCatalog(args).fields;

/** Await a read started after the write, including when a pre-write read was in flight. */
export const useRefreshIntegrationFields = () => {
  const dispatch = useAppDispatch();
  const store = useStore<DefaultRootState>();
  return useCallback(
    async (args: GetIntegrationFieldsArgs) => {
      const selectFields =
        integrationSlice.endpoints.getIntegrationFields.select(args);
      const waitForSettledFields = async () => {
        // Delayed invalidation can start a replacement request while the old
        // request resolves. Its initiate() promise may already be settled, so
        // wait for the cache and recheck after each replacement instead.
        while (selectFields(store.getState()).isLoading) {
          await new Promise<void>((resolve) => {
            const unsubscribe = store.subscribe(() => {
              if (!selectFields(store.getState()).isLoading) {
                unsubscribe();
                resolve();
              }
            });
          });
        }
        return selectFields(store.getState());
      };
      await waitForSettledFields();
      dispatch(
        integrationSlice.endpoints.getIntegrationFields.initiate(args, {
          subscribe: false,
          forceRefetch: true,
        })
      );
      const fields = await waitForSettledFields();
      if (fields.isError) throw fields.error;
      if (!fields.data) throw new Error('Fields could not be refreshed.');
      return fields.data;
    },
    [dispatch, store]
  );
};

export const useSelectFlatIntegrationsFields = (
  args: GetIntegrationsArgs
): IntegrationFieldWithId[] => useIntegrationFieldCatalog(args).flatFields;

type MappingSelectorArgs = TypedUseQueryStateResult<
  FieldMappingsResponse,
  GetFieldMappingsArgs,
  typeof BBK_FETCH_BASE_QUERY
>;

export const selectFields = (res: MappingSelectorArgs) => ({
  ...res,
  data: chain(res.data ?? [])
    .map(
      ({ name, standard, integration_id, type, field }) =>
        ({
          name,
          val: name,
          key: name,
          standard,
          type,
          is_lov: field?.is_lov,
          field_name: field?.name,
          integration_id,
          fallBackValue: '',
        }) as RTESuggestion
    )
    .sortBy((x) => x.name)
    .value(),
});

export const selectUrlFields = (res: MappingSelectorArgs) => ({
  ...res,
  data: selectFields(res).data.filter(
    (f) => f.type === IntegrationFieldTypeEnum.url
  ),
});

export const selectNonUrlFields = (res: MappingSelectorArgs) => ({
  ...res,
  data: selectFields(res).data.filter(
    (f) => f.type !== IntegrationFieldTypeEnum.url
  ),
});

export const RETENTION_OFFER_RESERVED_FIELD_NAME = 'Retention Offer';
export const OFFER_RESERVED_FIELDS: RTESuggestion[] = [
  {
    val: RETENTION_OFFER_RESERVED_FIELD_NAME,
    key: RETENTION_OFFER_RESERVED_FIELD_NAME,
    name: RETENTION_OFFER_RESERVED_FIELD_NAME,
    fallBackValue: '',
    type: AppFieldType.text,
    reserved: true,
  },
  {
    val: 'Days Since Offer Viewed',
    key: 'Days Since Offer Viewed',
    name: 'Days Since Offer Viewed',
    fallBackValue: '',
    type: AppFieldType.integer,
    reserved: true,
  },
  {
    val: 'Days Since Offer Accepted',
    key: 'Days Since Offer Accepted',
    name: 'Days Since Offer Accepted',
    fallBackValue: '',
    type: AppFieldType.integer,
    reserved: true,
  },
  {
    val: 'Days Since Offer Viewed But Not Accepted',
    key: 'Days Since Offer Viewed But Not Accepted',
    name: 'Days Since Offer Viewed But Not Accepted',
    fallBackValue: '',
    type: AppFieldType.integer,
    reserved: true,
  },
];

const RESERVED_FIELDS: RTESuggestion[] = [
  {
    val: 'Mobile Session',
    key: 'Mobile Session',
    name: 'Mobile Session',
    fallBackValue: '',
    type: AppFieldType.boolean,
    reserved: true,
  },
];

export const AI_RULE_RESERVED_KEY_PREFIX = 'ai.';
export const AI_RESERVED_CHURN_RISK_LEVEL = 'Churn Risk Level';
export const AI_RESERVED_CHURN_RISK_SCORE = 'Churn Risk Score';

export const AI_RESERVED_FIELDS: RTESuggestion[] = [
  {
    val: AI_RESERVED_CHURN_RISK_SCORE,
    key: AI_RESERVED_CHURN_RISK_SCORE,
    name: AI_RESERVED_CHURN_RISK_SCORE,
    fallBackValue: '',
    type: AppFieldType.integer,
    is_ai: true,
  },
  {
    val: AI_RESERVED_CHURN_RISK_LEVEL,
    key: AI_RESERVED_CHURN_RISK_LEVEL,
    name: AI_RESERVED_CHURN_RISK_LEVEL,
    fallBackValue: '',
    type: AppFieldType.text,
    is_ai: true,
  },
];

export const useSelectReservedFields = (): RTESuggestion[] => {
  const showOfferReservedFields = useFeatureFlags(
    FeatureFlagsE.SHOW_VIEWED_ACCEPTED_OFFER_RESERVED_FIELDS
  );
  const isDemoData = useShowPlayDemoData();
  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;
  const showAiReservedFields =
    useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE) ||
    ((agsLicense || growthV2License) && isDemoData);
  let reservedFields: RTESuggestion[] = [...RESERVED_FIELDS];

  if (showOfferReservedFields) {
    reservedFields = [...reservedFields, ...OFFER_RESERVED_FIELDS];
  }

  if (showAiReservedFields) {
    reservedFields = [...reservedFields, ...AI_RESERVED_FIELDS];
  }

  return reservedFields;
};

export const ALERT_FIELDS: RTESuggestion[] = [
  {
    fallBackValue: '',
    key: 'Company',
    name: 'Company',
    alert: true,
    type: 'text',
    val: 'Company',
  },
  {
    fallBackValue: '',
    key: 'Reason',
    name: 'Reason',
    alert: true,
    type: 'text',
    val: 'Reason',
  },
  {
    fallBackValue: '',
    key: 'Reason Other',
    name: 'Reason Other',
    alert: true,
    type: 'text',
    val: 'Reason Other',
  },
  {
    fallBackValue: '',
    key: 'Question',
    name: 'Question',
    alert: true,
    type: 'text',
    val: 'Question',
  },
  {
    fallBackValue: '',
    key: 'Competitor',
    name: 'Competitor',
    alert: true,
    type: 'text',
    val: 'Competitor',
  },
  {
    fallBackValue: '',
    key: 'Competitor Other',
    name: 'Competitor Other',
    alert: true,
    type: 'text',
    val: 'Competitor Other',
  },
  {
    fallBackValue: '',
    key: 'Comment',
    name: 'Comment',
    alert: true,
    type: 'text',
    val: 'Comment',
  },
  {
    fallBackValue: '',
    key: 'Sentiment',
    name: 'Sentiment',
    alert: true,
    type: 'text',
    val: 'Sentiment',
  },
  {
    fallBackValue: '',
    key: 'offer_name',
    name: 'offer_name',
    alert: true,
    type: 'text',
    val: 'offer_name',
  },
  {
    fallBackValue: '',
    key: 'offer_display_name',
    name: 'offer_display_name',
    alert: true,
    type: 'text',
    val: 'offer_display_name',
  },
  {
    fallBackValue: '',
    key: 'offer_type',
    name: 'offer_type',
    alert: true,
    type: 'text',
    val: 'offer_type',
  },
  {
    fallBackValue: '',
    key: 'offer_category',
    name: 'offer_category',
    alert: true,
    type: 'text',
    val: 'offer_category',
  },
  {
    fallBackValue: '',
    key: 'offer_content',
    name: 'offer_content',
    alert: true,
    type: 'text',
    val: 'offer_content',
  },
  {
    fallBackValue: '',
    key: 'Structured Feedback',
    name: 'Structured Feedback',
    alert: true,
    type: 'text',
    val: 'Structured Feedback',
  },
];

export const allowAlertFieldsForAgs = [
  'offer_name',
  'offer_display_name',
  'offer_objective',
  'offer_type',
  'offer_category',
  'offer_content',
];

export const useGetAlertFields = (): RTESuggestion[] => {
  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;
  return useMemo(() => {
    if (agsLicense || growthV2License) {
      const alertCopy = [...ALERT_FIELDS];

      const offerObjectiveField = {
        fallBackValue: '',
        key: 'offer_objective',
        name: 'offer_objective',
        alert: true,
        type: 'text',
        val: 'offer_objective',
      } satisfies RTESuggestion;
      const insertIndex = alertCopy.findIndex((af) => af.key === 'offer_type');
      alertCopy.splice(insertIndex, 0, offerObjectiveField);
      return alertCopy;
    }
    return ALERT_FIELDS;
  }, [agsLicense, growthV2License]);
};

export const createFieldsWithPrefixes = (allFields: RTESuggestion[]) => {
  const getPrefix = (field: RTESuggestion) => {
    if (field.reserved) return 'reserved';
    if (field.standard) return 'standard';
    if (field.alert) return 'alert';
    if (field.is_ai) return 'ai';
    return 'custom';
  };

  return allFields.map((f) => {
    const prefix = getPrefix(f);
    return {
      ...f,
      val: `${prefix}.${f.name}`,
    };
  });
};
export const useAvailableIntegrations = () => {
  const company = useSelector(selectCompany);
  const appKey = useSelector(selectAppId);
  const { growthV2License } = useGetProductLicense();
  const isStripeWebhookEnabled = useFeatureFlags(
    FeatureFlagsE.SHOW_STRIPE_WEBHOOK_INTEGRATION
  );
  return integrationSlice.endpoints.getIntegrations.useQuery(
    { appKey },
    {
      selectFromResult: (res) => ({
        ...res,
        data: res.data?.filter((integration) => {
          switch (integration.data_type) {
            // intercom and totango is deprecated
            case IntegrationDataType.INTERCOM:
            case IntegrationDataType.TOTANGO:
              return false;

            case IntegrationDataType.SALESFORCE:
              return !company.plan || canAccessSalesforceIntegration(company);

            case IntegrationDataType.STRIPE_WEBHOOK:
              return growthV2License && isStripeWebhookEnabled;

            default:
              return true;
          }
        }),
      }),
    }
  );
};

export const getJsIntegrationDisplayName = (
  integration?: Integration,
  billingIntegration?: Integration
) => {
  if (!integration) {
    return { integrationDisplayName: '', integrationIconName: '' };
  }

  const showChargebeeJs =
    !billingIntegration ||
    billingIntegration.data_type === IntegrationDataType.CHARGEBEE;

  const integrationDisplayName =
    integration.data_type === IntegrationDataType.BRIGHTBACKJS
      ? showChargebeeJs
        ? ChargebeeJsIntDisplayName
        : BrightbackJsIntDisplayName
      : integration.display_name;

  const integrationIconName =
    integration.data_type === IntegrationDataType.BRIGHTBACKJS
      ? ChargebeeJSIntKey
      : (integration.data_type.toLowerCase() as IconName);

  return { integrationDisplayName, integrationIconName };
};
